import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import { AlertTriangle, ArrowLeft, CheckCircle2, ChevronRight, Eye, ExternalLink, Rocket, Settings2, X } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingState, ErrorState, PermissionDeniedState } from "@/components/States";
import { Btn } from "@/components/Primitives";
import { api, formatApiError, formatApiErrorDetail, isPermissionError } from "@/lib/api";
import OverviewTab from "@/components/trip/OverviewTab";
import ItineraryTab from "@/components/trip/ItineraryTab";
import DocumentsTab from "@/components/trip/DocumentsTab";
import ConfigurationTab from "@/components/trip/ConfigurationTab";
import CopilotTab from "@/components/trip/CopilotTab";
import PackingTab from "@/components/trip/PackingTab";
import VendorsTab from "@/components/trip/VendorsTab";
import RunOverviewTab from "@/components/trip/RunOverviewTab";
import { useAuth } from "@/context/AuthContext";

const TRANSITIONS = {
  draft: ["published"],
  published: [],
  active: [],
  completed: [],
};
const BACKEND_TRIP_STATES = ["draft", "published", "active", "completed"];
const LIFECYCLE_STATES = ["planning", "active", "completed"];
const RUN_OVERVIEW_STATES = new Set(BACKEND_TRIP_STATES);
const PUBLISH_SETUP_KEYS = new Set(["configuration", "itinerary", "guides", "transportation", "accommodation"]);
const TABS = [
  { id: "overview", label: "Overview" },
  { id: "itinerary", label: "Itinerary" },
  { id: "vendors", label: "Vendors" },
  { id: "packing", label: "Packing" },
  { id: "documents", label: "Documents" },
  { id: "copilot", label: "AI Insights" },
  { id: "configuration", label: "Settings" },
];
const WORKSPACE_MODE_STORAGE_PREFIX = "trailpulse.tripWorkspaceMode.";
const TRIP_TEMPLATE_OPTIONS = [
  {
    id: "classic",
    name: "Original field guide",
    description: "The committed TrailPulse itinerary with side rails, day cards, and operations-focused detail.",
  },
  {
    id: "premium",
    name: "Editorial travel page",
    description: "The new immersive public and pre-active participant experience with batch-led booking.",
  },
  {
    id: "cinematic",
    name: "Cinematic luxury journal",
    description: "A dark, image-led destination story with a visual route and boutique departure carousel.",
  },
];

function asArray(value) {
  return Array.isArray(value) ? value : [];
}

function savedWorkspaceMode(tripId) {
  if (typeof window === "undefined") return null;
  try {
    const mode = window.localStorage.getItem(`${WORKSPACE_MODE_STORAGE_PREFIX}${tripId}`);
    return mode === "run" || mode === "setup" ? mode : null;
  } catch {
    return null;
  }
}

function saveWorkspaceMode(tripId, mode) {
  if (typeof window === "undefined" || !tripId) return;
  try {
    window.localStorage.setItem(`${WORKSPACE_MODE_STORAGE_PREFIX}${tripId}`, mode);
  } catch {
    // localStorage can be unavailable in privacy-restricted sessions.
  }
}

function workspaceCoverImage(trip) {
  const cover = trip?.cover_image;
  return typeof cover === "string" ? cover : cover?.url || "";
}

function publishSetupIssues(readiness) {
  return asArray(readiness?.items || readiness?.checklist)
    .filter((item) => PUBLISH_SETUP_KEYS.has(item.key) && ["warn", "blocker"].includes(item.status))
    .map((item) => ({
      key: item.key,
      label: item.label,
      status: item.status,
      detail: item.detail,
      tab: item.action?.tab,
      actionLabel: item.action?.label,
      allowed: item.action?.allowed,
      progress: item.progress_pct,
    }));
}

function publishPublicViewImpacts(trip, readiness) {
  const setupIssues = publishSetupIssues(readiness);
  const impacts = [];

  if (trip?.visibility !== "public") {
    impacts.push("Publishing prepares the public trip view, but it stays private until Public settings is switched to Public.");
  }

  if (setupIssues.some((item) => item.key === "itinerary")) {
    impacts.push("The public itinerary mirrors the participant itinerary, so missing days or activities will be visible as gaps.");
  }
  if (setupIssues.some((item) => item.key === "guides")) {
    impacts.push("Guide details may look incomplete on the public page.");
  }
  if (setupIssues.some((item) => item.key === "transportation")) {
    impacts.push("Transport details may be missing from the public itinerary.");
  }
  if (setupIssues.some((item) => item.key === "accommodation")) {
    impacts.push("Accommodation details may be missing from the public itinerary.");
  }

  return [...new Set(impacts)];
}

function lifecycleStateForTrip(trip) {
  const state = trip?.state;
  if (state === "active" || state === "completed") return state;
  return "planning";
}

function lifecycleLabel(state) {
  if (state === "completed") return "complete";
  return state;
}

function PublishReviewModal({ review, busy, selectedTemplate, onTemplateChange, onClose, onConfirm, onOpenSetup }) {
  useEffect(() => {
    if (!review?.open || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [review?.open]);

  if (!review?.open) return null;
  const setupIssues = publishSetupIssues(review.readiness);
  const impacts = publishPublicViewImpacts(review.trip, review.readiness);
  const hasWarnings = setupIssues.length > 0 || impacts.length > 0;

  const modal = (
    <div className="fixed inset-0 z-[1000] flex min-h-screen items-start justify-center overflow-y-auto bg-[#17211D]/35 p-4 backdrop-blur-sm sm:items-center" role="presentation" onClick={onClose}>
      <section
        data-testid="publish-preflight-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="publish-preflight-title"
        className="w-full max-w-3xl overflow-y-auto rounded-lg border border-white/70 bg-[#FFFAF1] p-5 shadow-2xl sm:max-h-[calc(100vh-2rem)]"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-[#2D7A65]">
              <Eye className="h-4 w-4" /> Publish check
            </p>
            <h2 id="publish-preflight-title" className="mt-2 font-[Plus_Jakarta_Sans,sans-serif] text-2xl font-black text-[#17211D]">Review before publishing</h2>
            <p className="mt-1 text-sm leading-6 text-[#516259]">These items can affect operations or what travellers see on the public page.</p>
          </div>
          <button type="button" aria-label="Close publish check" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[#516259] hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65]">
            <X className="h-4 w-4" />
          </button>
        </div>

        {review.loading ? (
          <div className="mt-5"><LoadingState label="Checking setup..." /></div>
        ) : (
          <>
            {!hasWarnings && (
              <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                <p className="flex items-center gap-2 font-black"><CheckCircle2 className="h-4 w-4" /> Setup and public view look ready.</p>
              </div>
            )}

            {setupIssues.length > 0 && (
              <div className="mt-5 rounded-lg border border-amber-200 bg-white p-4">
                <p className="flex items-center gap-2 text-sm font-black text-[#17211D]"><AlertTriangle className="h-4 w-4 text-amber-600" /> Missing setup</p>
                <div className="mt-3 divide-y divide-[#ECEAE4]">
                  {setupIssues.map((item) => (
                    <div key={item.key} className="grid gap-2 py-3 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
                      <div>
                        <p className="text-sm font-bold text-[#17211D]">{item.label}</p>
                        {item.detail && <p className="mt-0.5 text-xs leading-5 text-[#516259]">{item.detail}</p>}
                      </div>
                      <span className={`rounded-full px-2.5 py-1 text-[11px] font-black uppercase ${item.status === "blocker" ? "bg-rose-50 text-rose-700" : "bg-amber-50 text-amber-700"}`}>
                        {item.progress ?? 0}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {impacts.length > 0 && (
              <div className="mt-4 rounded-lg border border-[#E2DFD7] bg-white p-4">
                <p className="text-sm font-black text-[#17211D]">Public view impact</p>
                <ul className="mt-3 space-y-2">
                  {impacts.map((impact) => (
                    <li key={impact} className="flex gap-2 text-xs leading-5 text-[#516259]">
                      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600" />
                      <span>{impact}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mt-4 rounded-lg border border-[#E2DFD7] bg-white p-4">
              <p className="text-sm font-black text-[#17211D]">Choose the trip look</p>
              <p className="mt-1 text-xs leading-5 text-[#516259]">Open a real preview, then set the look you want travellers to see.</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {TRIP_TEMPLATE_OPTIONS.map((option) => {
                  const active = selectedTemplate === option.id;
                  return (
                    <div key={option.id} className={`rounded-lg border p-3 ${active ? "border-[#2D7A65] bg-emerald-50" : "border-[#E2DFD7] bg-[#FFFAF1]"}`}>
                      <p className="text-sm font-black text-[#17211D]">{option.name}</p>
                      <p className="mt-1 min-h-10 text-xs leading-5 text-[#516259]">{option.description}</p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <a
                          href={`/trips/${review.trip?.id}/public-preview?template=${option.id}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex min-h-9 items-center gap-1 rounded-md border border-[#D8D2C4] bg-white px-3 text-xs font-black text-[#17211D] hover:border-[#2D7A65]"
                        >
                          <ExternalLink className="h-3.5 w-3.5" /> Preview
                        </a>
                        <button
                          type="button"
                          onClick={() => onTemplateChange(option.id)}
                          className={`inline-flex min-h-9 items-center rounded-md px-3 text-xs font-black ${active ? "bg-[#2D7A65] text-white" : "bg-[#E5B85C] text-[#17211D]"}`}
                        >
                          {active ? "Selected" : "Use this look"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {review.error && <p className="mt-3 text-sm font-semibold text-rose-700">{review.error}</p>}

            <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Btn type="button" variant="secondary" onClick={onOpenSetup} disabled={busy}>Review setup</Btn>
              <Btn type="button" onClick={() => onConfirm(selectedTemplate)} disabled={busy}>
                <Rocket className="h-4 w-4" /> Publish anyway
              </Btn>
            </div>
          </>
        )}
      </section>
    </div>
  );

  if (typeof document === "undefined") return modal;
  return createPortal(modal, document.body);
}

export default function TripWorkspace() {
  const { id } = useParams();
  const nav = useNavigate();
  const { perms } = useAuth();
  const [trip, setTrip] = useState(null);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [denied, setDenied] = useState(false);
  const [tab, setTab] = useState("overview");
  const [dashboardTab, setDashboardTab] = useState("itinerary");
  const [workspaceMode, setWorkspaceMode] = useState(() => savedWorkspaceMode(id) || "setup");
  const [busy, setBusy] = useState(false);
  const [publishReview, setPublishReview] = useState({ open: false, loading: false });
  const [selectedPublishTemplate, setSelectedPublishTemplate] = useState("classic");
  const tripState = trip?.state;

  const load = useCallback(async () => {
    setError(false); setDenied(false); setErrorMessage("");
    try { const { data } = await api.get(`/trips/${id}`); setTrip(data); }
    catch (err) {
      const msg = formatApiError(err, "Could not load this trip.");
      setErrorMessage(msg);
      if (isPermissionError(err)) setDenied(true);
      else setError(true);
    }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!tripState) return;
    const nextMode = savedWorkspaceMode(id) || (tripState === "active" ? "run" : "setup");
    setWorkspaceMode(nextMode);
    saveWorkspaceMode(id, nextMode);
  }, [id, tripState]);

  const transition = async (to, extra = {}) => {
    setBusy(true);
    try { await api.post(`/trips/${id}/transition`, { to_state: to, ...extra }); toast.success(`Trip moved to ${to}`); await load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const openPublishReview = async () => {
    setSelectedPublishTemplate(trip?.itinerary_template || "classic");
    setPublishReview({ open: true, loading: true, trip });
    setBusy(true);
    try {
      const readinessResp = await api.get(`/trips/${id}/readiness`);
      setPublishReview({
        open: true,
        loading: false,
        trip,
        readiness: readinessResp.data,
        error: "",
      });
    } catch (err) {
      setPublishReview({ open: false, loading: false });
      toast.error(formatApiError(err, "Could not run publish checks."));
    } finally {
      setBusy(false);
    }
  };
  const confirmPublish = async (template = selectedPublishTemplate) => {
    setPublishReview({ open: false, loading: false });
    if (["published", "active", "completed"].includes(trip?.state)) {
      toast.success("Public view is already published.");
      await load();
      return;
    }
    await transition("published", { itinerary_template: template });
  };
  if (denied) return <AppLayout><PermissionDeniedState message={errorMessage} /></AppLayout>;
  if (error) return <AppLayout><ErrorState onRetry={load} message={errorMessage || "Could not load this trip."} /></AppLayout>;
  if (!trip) return <AppLayout><LoadingState label="Loading trip..." /></AppLayout>;

  const nexts = TRANSITIONS[trip.state] || [];
  const workflowState = lifecycleStateForTrip(trip);
  const canPublish = trip.can_publish && nexts.includes("published");
  const canEdit = trip.can_edit;
  const canOpenPublishReview = canEdit && (canPublish || ["published", "active", "completed"].includes(trip.state));
  const canParticipantView = perms.includes("participant:view");
  const canBookings = ["booking:manage", "finance:reconcile"].some((p) => perms.includes(p));
  const canFinance = perms.includes("finance:reconcile");
  const canDocs = canEdit || perms.includes("doc:review");
  const canCommunicate = perms.includes("comms:send");
  const canUseRunOverview = RUN_OVERVIEW_STATES.has(trip.state) && (canEdit || canCommunicate);
  const visibleTabs = TABS.filter((t) => {
    if (t.id === "vendors") return canEdit;
    if (t.id === "packing") return canEdit || canParticipantView;
    if (t.id === "documents") return canDocs;
    if (t.id === "copilot") return canParticipantView;
    if (t.id === "configuration") return canEdit;
    return true;
  });
  const activeTab = visibleTabs.some((t) => t.id === tab) ? tab : "overview";
  const showRunOverview = workspaceMode === "run" && canUseRunOverview;
  const coverImage = workspaceCoverImage(trip);
  const workspaceHeroStyle = coverImage ? {
    backgroundImage: `linear-gradient(135deg, rgba(23, 33, 29, 0.62), rgba(45, 122, 101, 0.16)), url(${JSON.stringify(coverImage)})`,
  } : undefined;
  const openSetup = (target = "overview") => {
    if (target === "communications") {
      openRunOverview(target);
      return;
    }
    setWorkspaceMode("setup");
    saveWorkspaceMode(id, "setup");
    if (target) setTab(target === "guides" ? "configuration" : target);
  };
  const openRunOverview = (target = "itinerary") => {
    setDashboardTab(target);
    setWorkspaceMode("run");
    saveWorkspaceMode(id, "run");
  };

  const tabProps = { trip, reload: load, canEdit, canPublish };

  return (
    <AppLayout allowViewportSticky={showRunOverview}>
      <div className="tp-page-wrap tp-trip-workspace-wrap">
        <button onClick={() => nav("/trips")} data-testid="back-to-trips" className="mb-1 flex min-h-7 items-center gap-1 rounded-xl text-sm font-bold text-[#516259] hover:text-[#2D7A65] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2">
          <ArrowLeft className="h-4 w-4" /> All trips
        </button>

        <div className="tp-hero mb-3 grid grid-cols-1 items-start gap-3 p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:p-5" style={workspaceHeroStyle}>
          <div className="min-w-0">
            <p className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#E5B85C]">{showRunOverview ? "Trip dashboard" : "Planner workspace"}</p>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="min-w-0 break-words font-[Plus_Jakarta_Sans,sans-serif] text-3xl font-extrabold leading-none sm:text-5xl" data-testid="trip-title">{trip.title}</h1>
            </div>
            <p className="mt-1 text-sm text-slate-500">{trip.type} · {trip.region || "—"} · {trip.duration_days} days · {trip.difficulty_grade}</p>
          </div>
          {(canEdit || canUseRunOverview) && (
            <div className="row-start-1 flex flex-wrap items-end justify-end gap-2 justify-self-end sm:col-start-2">
              {canUseRunOverview && (
                <Btn type="button" variant="secondary" className="min-h-10 w-auto px-3 py-1.5 text-xs" data-testid="workspace-mode-toggle" aria-label={showRunOverview ? "Setup" : "Trip dashboard"} aria-describedby="workspace-mode-caption" onClick={() => showRunOverview ? openSetup("overview") : openRunOverview()}>
                  {showRunOverview ? <Settings2 className="h-4 w-4 shrink-0" /> : <Eye className="h-4 w-4 shrink-0" />}
                  <span className="flex flex-col items-start text-left">
                    <span id="workspace-mode-caption" className="text-[10px] font-normal leading-3 text-[#17211D]/70">{showRunOverview ? "Edit details" : "View operations"}</span>
                    <span className="text-xs font-semibold leading-4">{showRunOverview ? "Setup" : "Trip dashboard"}</span>
                  </span>
                </Btn>
              )}
              {canOpenPublishReview && (
                <Btn className="min-h-10 px-3 py-1.5 text-xs" data-testid="publish-trip-action-button" aria-label="Publish" disabled={busy} onClick={openPublishReview}>
                  <Rocket className="h-4 w-4 shrink-0" />
                  <span className="text-xs font-semibold leading-4">Publish</span>
                </Btn>
              )}
            </div>
          )}
          <div data-testid="trip-lifecycle-stepper" aria-label="Trip lifecycle" className="col-span-full mt-1 flex w-full items-center gap-1 overflow-x-auto rounded-xl bg-white/75 px-2.5 py-1.5 backdrop-blur ring-1 ring-white/60">
            {LIFECYCLE_STATES.map((s, i) => {
              const done = LIFECYCLE_STATES.indexOf(workflowState) > LIFECYCLE_STATES.indexOf(s);
              const cur = workflowState === s;
              return (
                <div key={s} className="flex items-center gap-1">
                  <span className={`whitespace-nowrap rounded-full px-3 py-1 text-xs font-extrabold capitalize ${cur ? "bg-[#E5B85C] text-[#17211D] ring-1 ring-[#E5B85C]" : done ? "text-[#2D7A65]" : "text-[#516259]"}`}>{lifecycleLabel(s)}</span>
                  {i < LIFECYCLE_STATES.length - 1 && <ChevronRight className="h-3.5 w-3.5 text-[#516259]/35" />}
                </div>
              );
            })}
          </div>
        </div>

        {!showRunOverview && (
          <div className="tp-glass mb-4 flex items-center gap-1.5 overflow-x-auto rounded-[1.15rem] p-1.5" role="tablist" aria-label="Trip workspace sections">
            {visibleTabs.map((t) => (
              <button key={t.id} data-testid={`trip-tab-${t.id}`} onClick={() => setTab(t.id)}
                type="button"
                role="tab"
                aria-selected={activeTab === t.id}
                aria-controls={`tab-panel-${t.id}`}
                className={`min-h-9 whitespace-nowrap rounded-xl px-3 py-2 text-[13px] font-extrabold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C] focus-visible:ring-offset-2 ${activeTab === t.id ? "bg-[#E5B85C] text-[#17211D]" : "text-[#516259] hover:bg-white/60 hover:text-[#1A2420]"}`}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {trip.cloned_from_trip_id && (
          <div className="tp-paper mb-5 rounded-[1.75rem] border border-amber-200 p-4" data-testid="post-clone-review-checklist">
            <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-amber-900"><AlertTriangle className="h-4 w-4" /> Post-clone review</p>
            <div className="grid gap-2 md:grid-cols-2">
              {(trip.clone_review_checklist || []).map((item) => (
                <div key={item.key} className="rounded-2xl bg-white/70 px-3 py-2 text-xs text-amber-900">
                  <span className="font-semibold capitalize">{String(item.severity || "warning")}: </span>{item.label}
                </div>
              ))}
            </div>
            {(trip.clone_warnings || []).length > 0 && <p className="mt-2 text-xs text-amber-800">{trip.clone_warnings.join(" ")}</p>}
          </div>
        )}

        {showRunOverview ? (
          <RunOverviewTab
            {...tabProps}
            canCommunicate={canCommunicate}
            initialTab={dashboardTab}
            canParticipantView={canParticipantView}
            canBookings={canBookings}
            canFinance={canFinance}
            canDocs={canDocs}
            onSetup={openSetup}
            onOpenParticipants={() => nav(`/trips/${id}/participants`)}
            onOpenBookings={() => nav(`/trips/${id}/bookings`)}
            onOpenDocuments={() => nav(`/trips/${id}/documents`)}
          />
        ) : (
          <div id={`tab-panel-${activeTab}`} data-testid={`tab-panel-${activeTab}`} role="tabpanel" tabIndex={-1}>
            {activeTab === "overview" && <OverviewTab {...tabProps} onGoTab={openSetup} />}
            {activeTab === "itinerary" && <ItineraryTab {...tabProps} onGoTab={openSetup} />}
            {activeTab === "vendors" && <VendorsTab {...tabProps} />}
            {activeTab === "packing" && <PackingTab {...tabProps} />}
            {activeTab === "documents" && <DocumentsTab {...tabProps} />}
            {activeTab === "copilot" && <CopilotTab {...tabProps} />}
            {activeTab === "configuration" && <ConfigurationTab {...tabProps} />}
          </div>
        )}
        <PublishReviewModal
          review={publishReview}
          busy={busy}
          selectedTemplate={selectedPublishTemplate}
          onTemplateChange={setSelectedPublishTemplate}
          onClose={() => !busy && setPublishReview({ open: false, loading: false })}
          onConfirm={confirmPublish}
          onOpenSetup={() => {
            setPublishReview({ open: false, loading: false });
            openSetup("overview");
          }}
        />
      </div>
    </AppLayout>
  );
}
